/*
 * FEEDBACK-8 CART TITLES: CRO Cart line items in the site's two-line title format.
 *   line 1 = type + model (UPPERCASE), line 2 = colour (sentence case): "ПОЛОВИНКИ БАНСКИ GIA" / "Black".
 *
 * The split comes from snippets/pmd-product-title.liquid (mode 'json') via templates/product.pmd-title.liquid,
 * fetched as <Shopify.routes.root>products/<handle>?view=pmd-title -> {"main","sub"} or {"full"} (no split).
 *
 * CRO Cart renders the drawer with Preact inside the same-origin iframe#cro-cart-app-iframe. This script never
 * touches its text nodes or children: it only sets data-pmd-main / data-pmd-sub and the class pmd-ct on the
 * existing .cro_cart_item__title, and injects <style id="pmd-cart-titles"> into the iframe document that hides the
 * original text (font-size 0) and prints the two lines with ::before / ::after.
 *
 * Handle: .cro_cart_item[data-cro-line-key] -> /cart.js items[].key -> handle (fallback: items[].product_title ==
 * the title text). Lazy: nothing runs until the drawer first opens. Any failure leaves the original title.
 */
(function () {
  'use strict';
  if (window.__pmdCartTitles) return;
  window.__pmdCartTitles = true;

  var IFRAME_ID = 'cro-cart-app-iframe';
  var STYLE_ID = 'pmd-cart-titles';
  var MARK = 'pmd-ct';
  var STORE_PREFIX = 'pmd-ct:v1:';
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  if (root.charAt(root.length - 1) !== '/') root += '/';

  var titles = {}; // root+handle -> {main, sub} | false (no split / failed)
  var pending = {}; // root+handle -> true while fetching
  var keyToHandle = {};
  var titleToHandle = {};
  var cartFetch = null;
  var cartMisses = {}; // "key|text" already looked up in a /cart.js response that did not have it
  var applied = typeof WeakMap === 'function' ? new WeakMap() : null;
  var started = false;
  var doc = null;
  var observer = null;
  var scheduled = false;
  var metrics = null;

  function warn(e) {
    try { console.warn('[pmd-cart-titles]', e); } catch (_) {}
  }

  function cacheKey(handle) {
    return root + handle;
  }

  function readStore(key) {
    try {
      var raw = window.sessionStorage.getItem(STORE_PREFIX + key);
      return raw ? JSON.parse(raw) : undefined;
    } catch (_) {
      return undefined;
    }
  }

  function writeStore(key, value) {
    try { window.sessionStorage.setItem(STORE_PREFIX + key, JSON.stringify(value)); } catch (_) {}
  }

  function normalise(data) {
    if (!data || typeof data !== 'object') return false;
    if (typeof data.main === 'string' && typeof data.sub === 'string' && data.main.trim() && data.sub.trim()) {
      return { main: data.main.trim(), sub: data.sub.trim() };
    }
    return false; // {"full": ...} or anything unexpected: keep the cart's own title
  }

  function getTitle(handle) {
    var key = cacheKey(handle);
    if (Object.prototype.hasOwnProperty.call(titles, key)) return titles[key];
    var stored = readStore(key);
    if (stored !== undefined) {
      titles[key] = normalise(stored);
      return titles[key];
    }
    if (!pending[key]) {
      pending[key] = true;
      fetch(root + 'products/' + encodeURIComponent(handle) + '?view=pmd-title', { credentials: 'same-origin' })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (text) {
          // A missing template falls back to the normal product page (HTML): JSON.parse throws -> no split.
          var data = JSON.parse(text);
          titles[key] = normalise(data);
          writeStore(key, data);
        })
        .catch(function () {
          titles[key] = false; // memory only, so a later page view can retry
        })
        .then(function () {
          delete pending[key];
          schedule();
        });
    }
    return undefined;
  }

  function refreshCartMap(missKeys) {
    if (cartFetch) return;
    cartFetch = fetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (cart) {
        (cart && cart.items ? cart.items : []).forEach(function (item) {
          if (!item || !item.handle) return;
          if (item.key) keyToHandle[item.key] = item.handle;
          if (item.product_title) titleToHandle[String(item.product_title).trim()] = item.handle;
        });
      })
      .catch(warn)
      .then(function () {
        missKeys.forEach(function (k) { cartMisses[k] = true; });
        cartFetch = null;
        schedule();
      });
  }

  function px(value, fallback) {
    return /^-?\d+(\.\d+)?px$/.test(value) ? value : fallback;
  }

  function measure(title) {
    // Line 1 copies today's title typography (colour is inherited); line 2 the variant line's size and colour.
    var had = title.classList.contains(MARK);
    if (had) title.classList.remove(MARK); // synchronous: no paint in between
    var win = doc.defaultView;
    var t = win.getComputedStyle(title);
    var m = {
      size: px(t.fontSize, '14px'),
      weight: t.fontWeight || '400',
      spacing: t.letterSpacing && t.letterSpacing !== 'normal' ? t.letterSpacing : 'normal',
      line: px(t.lineHeight, '1.4'),
      subSize: '12px',
      subColor: '#7C7C80'
    };
    if (had) title.classList.add(MARK);
    var variant = doc.querySelector('.cro_cart_item__variant');
    if (variant) {
      var v = win.getComputedStyle(variant);
      m.subSize = px(v.fontSize, m.subSize);
      if (v.color) m.subColor = v.color;
    }
    return m;
  }

  function css(m) {
    // The cart's customCss sets '#app .cro_cart_item__title{font-size:14px !important}', so the rules need ID-level
    // specificity. :is(#app, html) has it without depending on #app; a browser without :is() drops all three rules
    // together, which leaves the original title.
    var t = ':is(#app, html) .cro_cart_item .cro_cart_item__title.' + MARK;
    return (
      t + '{font-size:0 !important;line-height:0 !important;letter-spacing:0 !important;}' +
      t + '::before{content:attr(data-pmd-main);content:attr(data-pmd-main) / "";display:block;' +
        'font-size:' + m.size + ' !important;font-weight:' + m.weight + ' !important;letter-spacing:' + m.spacing + ' !important;' +
        'line-height:' + m.line + ' !important;text-transform:uppercase !important;}' +
      t + '::after{content:attr(data-pmd-sub);content:attr(data-pmd-sub) / "";display:block;margin-top:2px;' +
        'font-size:' + m.subSize + ' !important;font-weight:400 !important;letter-spacing:normal !important;line-height:1.4 !important;' +
        'color:' + m.subColor + ' !important;text-transform:none !important;}'
    );
  }

  function ensureStyle(sample) {
    if (!metrics && sample) metrics = measure(sample);
    if (!metrics) return false;
    var style = doc.getElementById(STYLE_ID);
    var text = css(metrics);
    if (!style) {
      style = doc.createElement('style');
      style.id = STYLE_ID;
      (doc.head || doc.documentElement).appendChild(style);
    }
    if (style.textContent !== text) style.textContent = text;
    return true;
  }

  function unmark(title) {
    if (title.classList.contains(MARK)) title.classList.remove(MARK);
    if (title.hasAttribute('data-pmd-main')) title.removeAttribute('data-pmd-main');
    if (title.hasAttribute('data-pmd-sub')) title.removeAttribute('data-pmd-sub');
  }

  function mark(title, data) {
    if (title.getAttribute('data-pmd-main') !== data.main) title.setAttribute('data-pmd-main', data.main);
    if (title.getAttribute('data-pmd-sub') !== data.sub) title.setAttribute('data-pmd-sub', data.sub);
    if (!title.classList.contains(MARK)) title.classList.add(MARK);
  }

  function pass() {
    scheduled = false;
    if (!doc || !doc.body) return;
    var items = doc.querySelectorAll('.cro_cart_item');
    var misses = [];
    for (var i = 0; i < items.length; i++) {
      var title = items[i].querySelector('.cro_cart_item__title');
      if (!title) continue;
      try {
        var key = items[i].getAttribute('data-cro-line-key') || '';
        var text = (title.textContent || '').trim();
        var sig = key + '|' + text;
        if (applied && applied.get(title) !== sig) {
          unmark(title); // React reused this node for another line: drop stale lines until resolved
          applied.set(title, sig);
        }
        if (!title.classList.contains(MARK) && !metrics) ensureStyle(title);
        var handle = (key && keyToHandle[key]) || titleToHandle[text];
        if (!handle) {
          if (!cartMisses[sig]) misses.push(sig);
          continue;
        }
        var data = getTitle(handle);
        if (data === undefined) continue; // fetching; pass() runs again when it lands
        if (data && ensureStyle(title)) mark(title, data);
        else unmark(title);
      } catch (e) {
        try { unmark(title); } catch (_) {}
        warn(e);
      }
    }
    if (misses.length) refreshCartMap(misses);
  }

  function schedule() {
    if (scheduled || !started) return;
    scheduled = true;
    (window.requestAnimationFrame || window.setTimeout)(function () {
      try { pass(); } catch (e) { scheduled = false; warn(e); }
    });
  }

  function attach() {
    var iframe = document.getElementById(IFRAME_ID);
    var d = null;
    try { d = iframe && iframe.contentDocument; } catch (_) {}
    if (!d || !d.body) return false;
    if (d === doc) return true;
    doc = d;
    metrics = null;
    if (observer) observer.disconnect();
    observer = new MutationObserver(schedule);
    observer.observe(doc.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'data-cro-line-key']
    });
    try {
      doc.defaultView.addEventListener('resize', function () {
        metrics = null; // re-measure at the new breakpoint on the next pass
        var first = doc.querySelector('.cro_cart_item__title');
        if (first) ensureStyle(first);
      });
    } catch (_) {}
    return true;
  }

  function start() {
    started = true;
    var tries = 0;
    (function wait() {
      try {
        if (attach()) return schedule();
      } catch (e) {
        return warn(e);
      }
      if (++tries < 40) setTimeout(wait, 250);
    })();
  }

  function isOpen() {
    if (document.body && document.body.classList.contains('cro-cart-overflow-lock')) return true;
    var iframe = document.getElementById(IFRAME_ID);
    return !!(iframe && /translateX\(0(px)?\)/.test(iframe.style.transform || ''));
  }

  function onPageChange() {
    if (isOpen()) {
      if (!started) start();
      else attach() && schedule(); // re-attach if the cart runtime rebuilt the iframe document
    }
  }

  function init() {
    if (!document.body || typeof MutationObserver !== 'function' || typeof fetch !== 'function') return;
    var watchedIframe = null;
    var iframeObserver = new MutationObserver(onPageChange);
    var bodyObserver = new MutationObserver(function () {
      var iframe = document.getElementById(IFRAME_ID);
      if (iframe && iframe !== watchedIframe) {
        watchedIframe = iframe;
        iframeObserver.disconnect();
        iframeObserver.observe(iframe, { attributes: true, attributeFilter: ['style'] });
      }
      onPageChange();
    });
    // Body class (CRO Cart adds cro-cart-overflow-lock on open) and direct children (the iframe being inserted).
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'], childList: true });
    bodyObserver.takeRecords();
    var iframe = document.getElementById(IFRAME_ID);
    if (iframe) {
      watchedIframe = iframe;
      iframeObserver.observe(iframe, { attributes: true, attributeFilter: ['style'] });
    }
    onPageChange();
  }

  try {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
  } catch (e) {
    warn(e);
  }
})();
